import React, { createContext, useCallback, useContext, useState } from "react";
import { DriveSettingsFactory } from "@skbkontur/extern-core-js/dist/cjs/src/drive/settingsFactory";
import {
    IDriveContentUploadResult,
    IDriveUploadContentOptions,
} from "@skbkontur/extern-core-js/dist/cjs/src/drive/shared";
import { IProgress } from "@skbkontur/extern-core-js/dist/cjs/src/common/progress";
import { CancellationToken } from "@skbkontur/extern-core-js/dist/cjs/src/utils";
import { NativeDrive1Client } from "@skbkontur/extern-core-js/dist/cjs/src/drive/drive1client";

import { isTestEnvironment } from "../../../Engine/utils";
import { GetUserResponse } from "../../../../../Common/CandyApiClient/ICandyApiClient";

interface ExternDriveClientContext {
    initDrive: () => Promise<NativeDrive1Client>;
    nativeDrive1Client: NativeDrive1Client | null;
}

interface ExternDriveClientProviderProps {
    children: React.ReactNode;
    getPortalUser: () => Promise<GetUserResponse>;
}

interface UploadParams {
    file: File;
    options?: IDriveUploadContentOptions;
    cancellationToken?: CancellationToken;
    progress?: IProgress;
}
export type Upload = (params: UploadParams) => Promise<IDriveContentUploadResult>;

const ExternDriveClientContext = createContext<ExternDriveClientContext | null>(null);

export const ExternDriveClientProvider = ({ children, getPortalUser }: ExternDriveClientProviderProps) => {
    const [nativeDrive1Client, setNativeDrive1Client] = useState<NativeDrive1Client | null>(null);

    const initDrive = useCallback(
        () =>
            new Promise<NativeDrive1Client>((resolve, reject) => {
                const settings = isTestEnvironment()
                    ? DriveSettingsFactory.getForDrive1Test()
                    : DriveSettingsFactory.getForDrive1();

                getPortalUser().then(portalUser => {
                    if (!portalUser.Id) {
                        reject(new Error("portalUserId is missing"));
                        return;
                    }

                    const driveClient = new NativeDrive1Client(settings, portalUser.Id);

                    setNativeDrive1Client(driveClient);
                    resolve(driveClient);
                }, reject);
            }),
        [getPortalUser]
    );

    return (
        <ExternDriveClientContext.Provider value={{ initDrive: initDrive, nativeDrive1Client: nativeDrive1Client }}>
            {children}
        </ExternDriveClientContext.Provider>
    );
};

export const useExternDriveClient = () => {
    const ctx = useContext(ExternDriveClientContext);

    if (!ctx) {
        throw new Error("useExternDriveClient must be used as an ExternDriveClientProvider");
    }

    const { nativeDrive1Client, initDrive } = ctx;

    const getDriveClient = useCallback(async () => {
        if (nativeDrive1Client) {
            return nativeDrive1Client;
        }

        return await initDrive();
    }, [nativeDrive1Client, initDrive]);

    const upload: Upload = useCallback(
        async ({
            file,
            options = { daysToLive: 30, srv: "candyFront" }, // для srv CandyFront, бэк у себя хранит копию
            cancellationToken,
            progress,
        }) => {
            const driveClient = await getDriveClient();

            return driveClient.upload(file, options, cancellationToken, progress);
        },
        [nativeDrive1Client]
    );

    return {
        ...ctx,
        upload: upload,
    };
};
